/**
 * Single source of truth for the external-expert connector guides.
 *
 * The hub page, every host guide and the docs catalogue read availability from
 * here, so a vendor decision is recorded once. Only change a `status` after the
 * vendor itself shows the new state (a published listing, a merged catalogue
 * entry) — a submission is not a publication.
 *
 * Only hosts a user can connect today belong here, through a directory listing
 * or a manual setup we have tested. Do not add a guide for a host whose only
 * route is a pending application.
 *
 * Logos are Iconify ids rendered by the same `@iconify-icon/react` component the
 * docs already use. Provenance: `simple-icons:*` comes from Simple Icons
 * (https://simpleicons.org, CC0 icon data) and `logos:grok-icon` from SVG Logos
 * (https://github.com/gilbarbara/logos, CC0). Both collect the vendors' own
 * published marks; the trademarks remain their owners'. A host with no verified
 * mark in those sets gets a neutral glyph instead of a drawn-up logo.
 */

import PageLink from '@/utils/PageLink';

export const CHATCO_MCP_URL = 'https://mcp.chat.co/mcp';
export const CHATCO_MCP_SCOPE = 'chatco:read';

/**
 * Date the status evidence behind the entries below (vendor listings, our
 * submissions and our own setup tests) was last reviewed. It is not a claim that
 * every vendor was re-checked on that date; a status we could not confirm is
 * worded as unconfirmed.
 */
export const CONNECTOR_STATUS_CHECKED = '30 September 2026';

export type ConnectorStatusTone = 'live' | 'review' | 'manual';

export interface ConnectorHost {
  slug: string;
  name: string;
  /** Which products or surfaces the guide covers. */
  surfaces: string;
  href: string;
  /** Iconify id of the vendor's published mark. Omitted when none is verified. */
  logo?: string;
  /** Brand colour for single-colour marks; defaults to slate-900. */
  logoColor?: string;
  /** Neutral glyph used when there is no verified logo. */
  fallbackIcon?: string;
  tone: ConnectorStatusTone;
  /** Short availability label, e.g. "Published". */
  status: string;
  /** One-sentence explanation of the availability label. */
  statusDetail: string;
  /** How a user actually connects today. */
  howToConnect: string;
  /** Docs catalogue fields. */
  readingTime: string;
  keywords: string[];
}

export const CONNECTOR_TONE_LABEL: Record<ConnectorStatusTone, string> = {
  live: 'Published',
  review: 'In vendor review',
  manual: 'Manual setup',
};

export const CONNECTOR_HOSTS: ConnectorHost[] = [
  {
    slug: 'claude',
    name: 'Claude',
    surfaces: 'Claude web, Desktop, mobile and Claude Code',
    href: PageLink.docsExternalExpertsClaude,
    logo: 'simple-icons:claude',
    logoColor: '#D97757',
    tone: 'live',
    status: 'Published in the Claude connector directory',
    statusDetail:
      'Chat.co is listed in Claude’s connector directory. Claude Code can also add the same server manually.',
    howToConnect: 'Directory → Connect',
    readingTime: '8 min',
    keywords: ['anthropic', 'claude code', 'claude desktop', 'connector directory'],
  },
  {
    slug: 'chatgpt',
    name: 'ChatGPT',
    surfaces: 'ChatGPT on the web, developer mode',
    href: PageLink.docsExternalExpertsChatgpt,
    logo: 'simple-icons:openai',
    tone: 'review',
    status: 'Directory submission in review',
    statusDetail:
      'The public ChatGPT listing is not published yet. You can connect today as a custom connection in developer mode, where your plan allows it.',
    howToConnect: 'Developer mode → add server URL',
    readingTime: '8 min',
    keywords: ['openai', 'developer mode', 'plugins', 'custom connector'],
  },
  {
    slug: 'cursor',
    name: 'Cursor',
    surfaces: 'Cursor editor (agent)',
    href: PageLink.docsExternalExpertsCursor,
    logo: 'simple-icons:cursor',
    tone: 'manual',
    status: 'Community listing live; official marketplace not confirmed',
    statusDetail:
      'A community listing on cursor.directory is live. Chat.co has applied to Cursor’s official marketplace; no decision has been confirmed.',
    howToConnect: 'cursor.directory or mcp.json',
    readingTime: '7 min',
    keywords: ['mcp.json', 'cursor directory', 'ide', 'editor'],
  },
  {
    slug: 'codex',
    name: 'Codex',
    surfaces: 'Codex CLI, IDE extension and app',
    href: PageLink.docsExternalExpertsCodex,
    logo: 'simple-icons:openai',
    tone: 'manual',
    status: 'Manual configuration',
    statusDetail:
      'Codex connects to the server through its own MCP configuration. There is no separate Codex directory listing.',
    howToConnect: 'codex mcp add → login',
    readingTime: '7 min',
    keywords: ['openai', 'codex cli', 'config.toml', 'terminal'],
  },
  {
    slug: 'grok',
    name: 'Grok',
    surfaces: 'grok.com',
    href: PageLink.docsExternalExpertsGrok,
    logo: 'logos:grok-icon',
    tone: 'manual',
    status: 'Custom connector',
    statusDetail:
      'Add Chat.co as a custom connector on grok.com; we have tested that setup. A listing in Grok’s public connector catalogue is not confirmed.',
    howToConnect: 'grok.com/connectors → Custom',
    readingTime: '6 min',
    keywords: ['xai', 'grok.com', 'custom connector'],
  },
  {
    slug: 'grok-build',
    name: 'Grok Build',
    surfaces: 'Grok Build terminal agent',
    href: PageLink.docsExternalExpertsGrokBuild,
    logo: 'logos:grok-icon',
    tone: 'manual',
    status: 'Manual plugin install; catalogue entry in review',
    statusDetail:
      'The plugin is public on GitHub. Its entry in xAI’s plugin marketplace is an open pull request awaiting maintainer review.',
    howToConnect: 'Clone plugin or grok mcp add',
    readingTime: '8 min',
    keywords: ['xai', 'grok cli', 'plugin', 'marketplace', 'terminal'],
  },
];

export const getConnectorHost = (slug: string): ConnectorHost => {
  const host = CONNECTOR_HOSTS.find((candidate) => candidate.slug === slug);
  if (!host) throw new Error(`Unknown connector host: ${slug}`);
  return host;
};

const OVERVIEW_LINK = { title: 'Experts in your AI apps', href: PageLink.docsExternalExperts };

/** Previous/next links that walk the overview and then each host guide in order. */
export const getConnectorNeighbours = (slug: string) => {
  const index = CONNECTOR_HOSTS.findIndex((host) => host.slug === slug);
  const prev = CONNECTOR_HOSTS[index - 1];
  const next = CONNECTOR_HOSTS[index + 1];
  return {
    prevArticle: prev ? { title: `${prev.name} setup`, href: prev.href } : OVERVIEW_LINK,
    nextArticle: next ? { title: `${next.name} setup`, href: next.href } : OVERVIEW_LINK,
  };
};
